import React, { useEffect } from 'react';

interface ScorePopupProps {
    points: number;
    text: string;
    footer: string;
    onClose: () => void;
    style?: 'coins' | 'money';
    preText?: string;
    postText?: string;
}

const ScorePopup: React.FC<ScorePopupProps> = ({ points, text, footer, onClose, style = 'coins', preText = '', postText = '' }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 2000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const [displayPoints, setDisplayPoints] = React.useState(0);

    useEffect(() => {
        const duration = 700; // 0.7 second animation
        const steps = 30;
        const increment = points / steps;
        const stepTime = duration / steps;
        let current = 0;

        const timer = setInterval(() => {
            current += increment;
            if (current >= points) {
                setDisplayPoints(points);
                clearInterval(timer);
            } else {
                setDisplayPoints(Math.floor(current));
            }
        }, stepTime);

        return () => clearInterval(timer);
    }, [points]);

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-3xl p-8 w-full max-w-sm mx-4 shadow-2xl transform animate-scaleIn flex flex-col items-center text-center relative">
                {/* Icon */}
                {style === 'coins' ? (
                    <div className="w-24 h-24 bg-yellow-50 rounded-full flex items-center justify-center mb-6 relative">
                        <div className="absolute inset-0 border-4 border-yellow-200 rounded-full border-t-yellow-500 animate-spin-slow" style={{ animationDuration: '3s', transform: 'rotate(310deg)' }}></div>
                        <div className="w-12 h-12 rounded-full bg-yellow-400 border-2 border-yellow-500 flex items-center justify-center shadow-sm">
                            <svg className="w-6 h-6 text-yellow-700" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                            </svg>
                        </div>
                    </div>
                ) : (
                    <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mb-6 relative">
                        <div className="absolute inset-0 border-4 border-green-200 rounded-full border-t-green-500 animate-spin-slow" style={{ animationDuration: '3s' }}></div>
                        <span className="text-green-500 font-bold text-5xl">$</span>
                    </div>
                )}

                {/* Score Box */}
                <div className={`${style === 'coins' ? 'bg-yellow-50 border-yellow-100' : 'bg-green-50 border-green-100'} border rounded-xl p-6 w-full mb-6`}>
                    <div className={`text-4xl font-bold ${style === 'coins' ? 'text-yellow-600' : 'text-green-700'} mb-2`}>
                        + {preText}{displayPoints}{postText}
                    </div>
                    <div className={`${style === 'coins' ? 'text-yellow-700' : 'text-green-800'} font-medium text-sm`}>
                        {text}
                    </div>
                </div>

                {/* Footer */}
                <div className="text-slate-500 font-medium text-sm">
                    {footer}
                </div>
            </div>
            <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-scaleIn { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
        </div>
    );
};

export default ScorePopup;
